import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Rocket, Zap, Shield, Users, Euro, Calendar, 
  TrendingUp, Crown, ArrowRight, Sparkles, Search, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from '../i18n';
import LanguageSelector from '../components/LanguageSelector';

// Import refactored landing components
import {
  AnimatedPitch,
  AnimatedCounter,
  ParticlesBackground,
  GlowingOrb,
  AdvantageCard,
  FulguratingLogo,
  LANDING_COLORS
} from '../components/landing';

// Main Component
export default function CoachLandingPage() {
  const { t } = useTranslation();
  const [ambassadorCount, setAmbassadorCount] = useState(11); // Default to 11
  const totalSpots = 90;
  const remainingSpots = totalSpots - ambassadorCount;

  // Fetch real ambassador count from API
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ambassador-count`);
        if (response.ok) {
          const data = await response.json();
          // API already returns displayed count with +11 offset
          setAmbassadorCount(data.current || 11);
        }
      } catch (error) {
        console.log('Using default count');
      }
    };
    fetchCount();
  }, []);

  const advantages = [
    {
      icon: Shield,
      title: t('coachLanding.zeroRisk'),
      description: t('coachLanding.zeroRiskDesc'),
      color: LANDING_COLORS.accent,
    },
    {
      icon: Euro,
      title: t('coachLanding.commissionHalved'),
      description: t('coachLanding.commissionHalvedDesc'),
      color: LANDING_COLORS.primary,
    },
    {
      icon: Users,
      title: t('coachLanding.clientsProvided'),
      description: t('coachLanding.clientsProvidedDesc'),
      color: '#3b82f6',
    },
    {
      icon: Calendar,
      title: t('coachLanding.flexibleSchedule'),
      description: t('coachLanding.flexibleScheduleDesc'),
      color: '#f59e0b',
    },
    {
      icon: TrendingUp,
      title: t('coachLanding.guaranteedIncome'),
      description: t('coachLanding.guaranteedIncomeDesc'),
      color: '#ec4899',
    },
    {
      icon: Crown,
      title: t('coachLanding.founderStatus'),
      description: t('coachLanding.founderStatusDesc'),
      color: '#8b5cf6',
    },
  ];

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: `linear-gradient(135deg, ${LANDING_COLORS.darker} 0%, ${LANDING_COLORS.dark} 50%, #1e3a5f 100%)` }}>
      
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="text-white font-bold text-xl tracking-tight">
              MOTIVYA
            </Link>
            
            {/* Right side - Discover + Language */}
            <div className="flex items-center gap-3">
              {/* Discover Motivya Link - Goes to B2C Coming Soon */}
              <Link 
                to="/explore"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-all border border-white/20"
                data-testid="discover-motivya-link"
              >
                <Search className="w-4 h-4" />
                {t('coachLanding.discoverMotivya')}
              </Link>
              
              {/* Language Selector */}
              <LanguageSelector />
            </div>
          </div>
        </div>
      </header>

      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <GlowingOrb delay={0} size={400} color={LANDING_COLORS.primary} />
        <div className="absolute top-1/4 right-0">
          <GlowingOrb delay={1} size={300} color="#3b82f6" />
        </div>
        <div className="absolute bottom-0 left-1/4">
          <GlowingOrb delay={2} size={350} color={LANDING_COLORS.accent} />
        </div>
        <ParticlesBackground />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 py-20 pt-24">

        {/* Logo */}
        <FulguratingLogo />
        
        {/* Brand Name with Lightning Effect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="relative mt-6 mb-6"
        >
          {/* Electric glow behind text */}
          <motion.div
            className="absolute inset-0 blur-2xl"
            style={{ background: `linear-gradient(90deg, ${LANDING_COLORS.primary}50, #22c55e50, ${LANDING_COLORS.primary}50)` }}
            animate={{
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <h1
            className="relative text-5xl md:text-7xl font-black tracking-tight"
            style={{ color: LANDING_COLORS.white }}
          >
            MOTIVYA
          </h1>
        </motion.div>

        {/* Animated Story Pitch - Replaces simple tagline */}
        <AnimatedPitch t={t} />

        {/* Selection Badge - Clean Effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, type: "spring" }}
          className="relative mb-8"
        >
          {/* Pulsing glow */}
          <motion.div
            className="absolute inset-0 rounded-full blur-xl"
            style={{ background: LANDING_COLORS.primary }}
            animate={{ 
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <div 
            className="relative px-8 py-4 rounded-full font-bold text-lg flex items-center gap-3 border-2"
            style={{ background: LANDING_COLORS.dark, color: LANDING_COLORS.primary, borderColor: LANDING_COLORS.primary }}
          >
            <motion.span
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              <Sparkles className="w-6 h-6" />
            </motion.span>
            {t('coachLanding.selectedBadge')}
            <motion.span
              animate={{ rotate: [0, -15, 15, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              <Sparkles className="w-6 h-6" />
            </motion.span>
          </div>
        </motion.div>

        {/* Main Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            <span style={{ color: LANDING_COLORS.primary }}>{totalSpots}</span> {t('coachLanding.ambassadorSpots')}
          </h2>
          <p className="text-xl text-white/80">
            {t('coachLanding.inBrussels')}
          </p>
        </motion.div>

        {/* Counter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-10 w-full max-w-md"
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-white/70">{t('coachLanding.reservedSpots')}</span>
            <span className="text-2xl font-bold" style={{ color: LANDING_COLORS.primary }}>
              <AnimatedCounter end={ambassadorCount} /> / {totalSpots}
            </span>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${LANDING_COLORS.primary}, ${LANDING_COLORS.accent})` }}
              initial={{ width: 0 }}
              animate={{ width: `${(ambassadorCount / totalSpots) * 100}%` }}
              transition={{ duration: 1.5, delay: 1.3 }}
            />
          </div>
          <p className="text-center text-white/60 text-sm mt-3">
            {t('coachLanding.onlyRemaining').replace('{count}', remainingSpots)}
          </p>
        </motion.div>

        {/* CTA Button - Electric Effect */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, type: "spring" }}
          className="relative mb-20"
        >
          {/* Electric arcs around button */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`arc-${i}`}
              className="absolute w-full h-full"
              style={{
                border: `2px solid ${LANDING_COLORS.primary}`,
                borderRadius: '1rem',
                transform: `scale(${1 + i * 0.1})`,
              }}
              animate={{
                opacity: [0, 0.5, 0],
                scale: [1 + i * 0.1, 1.3 + i * 0.1, 1 + i * 0.1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
          
          <Link to="/become-coach#form">
            <Button
              className="relative text-xl md:text-2xl px-10 md:px-14 py-7 md:py-8 font-bold rounded-2xl shadow-2xl overflow-hidden group"
              style={{ background: LANDING_COLORS.primary, color: LANDING_COLORS.dark }}
              data-testid="cta-become-coach"
            >
              {/* Shine effect */}
              <motion.div
                className="absolute inset-0"
                style={{ background: `linear-gradient(90deg, transparent, ${LANDING_COLORS.white}50, transparent)` }}
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              />
              {/* Lightning bolt effect */}
              <motion.div
                className="absolute inset-0"
                style={{ 
                  background: `radial-gradient(circle at center, ${LANDING_COLORS.white}30, transparent 50%)`,
                }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 3 }}
              />
              <span className="relative flex items-center gap-3">
                <motion.span
                  animate={{ rotate: [0, 15, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Rocket className="w-6 h-6 md:w-7 md:h-7" />
                </motion.span>
                {t('coachLanding.ctaButton')}
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                >
                  <ArrowRight className="w-6 h-6 md:w-7 md:h-7" />
                </motion.span>
              </span>
            </Button>
          </Link>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 left-0 right-0 flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-white/40 text-sm flex flex-col items-center gap-2"
          >
            <span>{t('coachLanding.discoverMore')}</span>
            <ArrowRight className="w-5 h-5 rotate-90" />
          </motion.div>
        </motion.div>
      </section>

      {/* Advantages Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ background: `${LANDING_COLORS.primary}20`, color: LANDING_COLORS.primary }}
            >
              <Zap className="w-4 h-4" />
              {t('coachLanding.whyJoin')}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t('coachLanding.advantagesTitle')}
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              {t('coachLanding.advantagesSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage, index) => (
              <AdvantageCard
                key={index}
                {...advantage}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-20 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('coachLanding.howItWorksTitle')}
            </h2>
          </motion.div>

          <div className="space-y-8">
            {[
              { step: 1, icon: Users, title: t('coachLanding.step1Title'), desc: t('coachLanding.step1Desc') },
              { step: 2, icon: Calendar, title: t('coachLanding.step2Title'), desc: t('coachLanding.step2Desc') },
              { step: 3, icon: Euro, title: t('coachLanding.step3Title'), desc: t('coachLanding.step3Desc') },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="flex items-center gap-6"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 font-bold text-2xl"
                  style={{ background: LANDING_COLORS.primary, color: LANDING_COLORS.dark }}
                >
                  {item.step}
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-5 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-white/60">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Detailed explanation button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link to="/become-coach">
              <Button
                variant="outline"
                className="text-lg px-8 py-4 rounded-xl border-white/30 hover:bg-white/10 text-white"
                data-testid="how-it-works-details-btn"
              >
                <HelpCircle className="w-5 h-5 mr-2" />
                {t('coach.howItWorksButton')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-10 md:p-16"
          >
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Crown className="w-16 h-16 mx-auto mb-6" style={{ color: LANDING_COLORS.primary }} />
            </motion.div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('coachLanding.finalCtaTitle')}
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
              {t('coachLanding.finalCtaSubtitle')}
            </p>

            <Link to="/become-coach#form">
              <Button
                className="text-xl px-12 py-7 font-bold rounded-xl"
                style={{ background: LANDING_COLORS.primary, color: LANDING_COLORS.dark }}
              >
                <Rocket className="w-6 h-6 mr-3" />
                {t('coachLanding.ctaButton')}
              </Button>
            </Link>

            <p className="text-white/40 text-sm mt-6">
              {t('coachLanding.freeNoCommitment')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © 2026 Motivya. {t('coach.allRightsReserved')}
          </p>
        </div>
      </footer>
    </div>
  );
}
